import type { ReactNode } from 'react'
import type { TrackingStats } from '~/gql/scorm/scorm'
import { CircleCheckBig, CircleDashed, CircleHelp, Clock, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import CompletionRing from '~/pages/backoffice/admin/dashboard/CompletionRing'
import { percent, VIZ } from '~/pages/backoffice/admin/dashboard/viz'

/**
 * Avancement d'un périmètre : anneau de complétion, effectif, répartition par
 * état. Le tableau de bord (#138) le montre sur toute l'organisation, la fiche
 * session (#243) sur une session — mêmes compteurs, même lecture.
 */
type IProps = {
  stats: TrackingStats | undefined
  scopeLabel: string
  /** Notes propres à l'écran, posées sous la répartition. */
  notes?: ReactNode
}

function CompletionBreakdown({ stats, scopeLabel, notes }: IProps) {
  const { t } = useTranslation()
  const total = stats?.total ?? 0
  const learners = stats?.learners ?? 0
  /**
   * Segments de la répartition. `icon` n'est pas décoratif : l'ambre « en
   * cours » passe sous 3:1 sur fond clair dans la palette de statut, et sa
   * contrepartie obligatoire est le couple icône + libellé (cf. `viz.ts`).
   */
  const segments = [
    { key: 'completed', label: t('status.completed'), icon: CircleCheckBig, value: stats?.completed ?? 0, color: VIZ.good },
    { key: 'inProgress', label: t('status.incomplete'), icon: Clock, value: stats?.inProgress ?? 0, color: VIZ.warning },
    { key: 'notStarted', label: t('status.not_attempted'), icon: CircleDashed, value: stats?.notStarted ?? 0, color: VIZ.muted },
    // Modules pas encore ouverts (#182), à part du « non commencé » : un module
    // qui ouvre le 15 septembre n'est pas du retard le 1er, et le fondre dans
    // le retard ferait passer une promo à jour pour une promo décrochée.
    { key: 'notYetUnlocked', label: t('stats.dashboard.notYetUnlocked'), icon: Lock, value: stats?.notYetUnlocked ?? 0, color: VIZ.serious },
    // N'apparaît que si un contenu n'a jamais posé de statut exploitable :
    // le fondre dans « non commencé » ferait passer un trou de données pour
    // un fait pédagogique.
    { key: 'unknown', label: t('status.unknown'), icon: CircleHelp, value: stats?.completionUnknown ?? 0, color: VIZ.critical },
  ].filter(segment => !['unknown', 'notYetUnlocked'].includes(segment.key) || segment.value > 0)

  const completionRate = percent(stats?.completed ?? 0, total)

  if (learners === 0)
    return <p className="text-sm text-muted-foreground">{t('stats.dashboard.noEnrollments')}</p>

  return (
    <>
      <div className="flex flex-wrap items-center gap-6">
        {total > 0 && (
          <div className="relative flex items-center justify-center">
            <CompletionRing value={completionRate} />
            {/*
                    Chiffre héros : ≥ 48 px et en chiffres PROPORTIONNELS.
                    Les `tabular-nums` donnent à chaque chiffre la largeur
                    d'un zéro, ce qui fait bâiller un « 12 % » à cette
                    taille — on les réserve aux colonnes qui s'alignent.
                  */}
            <div className="absolute flex flex-col items-center">
              <span className="text-5xl font-semibold leading-none">{completionRate}</span>
              <span className="mt-1 text-xs text-muted-foreground">%</span>
            </div>
          </div>
        )}

        <dl className="min-w-0">
          <dt className="text-sm text-muted-foreground">{scopeLabel}</dt>
          <dd className="text-2xl font-semibold">{learners}</dd>
        </dl>
      </div>

      {/* Inscrits à des sessions encore vides : on les compte, mais il
                n'y a rien à répartir — une barre à 0 se lirait comme un retard. */}
      {total === 0
        ? <p className="text-sm text-muted-foreground">{t('stats.dashboard.noModules')}</p>
        : (
            <>
              {/*
                      Barre empilée : la forme juste pour des parts d'un tout. Les
                      segments sont séparés par un vide de 2 px laissant voir le
                      fond — pas par un contour : un trait autour d'une marque
                      ajoute de l'encre qui n'est pas de la donnée.
                    */}
              <div className="flex h-3 w-full max-w-xl gap-[2px] overflow-hidden rounded-full">
                {segments.filter(segment => segment.value > 0).map(segment => (
                  <div
                    key={segment.key}
                    className="first:rounded-l-full last:rounded-r-full"
                    style={{
                      width: `${(segment.value / total) * 100}%`,
                      backgroundColor: segment.color,
                    }}
                    role="presentation"
                  />
                ))}
              </div>

              <ul className="flex flex-wrap gap-x-6 gap-y-2">
                {segments.map(segment => (
                  <li key={segment.key} className="flex items-center gap-2 text-sm">
                    <segment.icon className="size-4 shrink-0" style={{ color: segment.color }} />
                    <span>{segment.label}</span>
                    <span className="font-medium tabular-nums">{segment.value}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {`(${percent(segment.value, total)} %)`}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Le chiffre ci-dessus compte des personnes, la répartition des
                        modules (#245) : sans cette ligne, les deux se liraient sur
                        la même base. */}
              <p className="text-xs text-muted-foreground">
                {t('stats.dashboard.breakdownBasis', { modules: total })}
              </p>
            </>
          )}

      {notes}

      {!!stats?.notYetUnlocked && (
        <p className="text-xs text-muted-foreground">
          {t('stats.dashboard.notYetUnlocked.hint', { modules: stats.notYetUnlocked })}
        </p>
      )}
    </>
  )
}

export default CompletionBreakdown
